import { nullish } from "helpers/nullish";
import { ValueOf } from "lib/ts-utility";
import { ValueTypes, ValueTypesToMerchantTableMappings } from "constants/product-collection";
import { safeParseInt } from "lib/number";
import { ILookup, ILookupWhereField, IValueTypeMap } from "./types";

interface IStatementCondition {
    AND: "and";
    OR: "or";
    NOT: "not";
}

interface IStatementEquationOperator {
    EQ: "eq";
    LT: "lt";
    GT: "gt";
    LE: "le";
    GE: "ge";
    NE: "ne";
}

interface IStatementOrderDirection {
    ASC: "ASC";
    DESC: "DESC";
}

type ConditionType = ValueOf<IStatementCondition>;

type OperatorType = ValueOf<IStatementEquationOperator>;

export type OrderDirectionType = ValueOf<IStatementOrderDirection>;

export enum AggregationFunctions {
    StringAggregation = "string_aggr",
}

export interface IAggregationGroupType {
    type: AggregationFunctions;
    string_aggr?: {
        delimiter?: string;
    };
}

export interface IClauseType {
    field: string;
    value: string;
    operator: OperatorType;
    value_type?: ValueTypes;
}

interface IQueryType {
    rules: Array<IQueryType | IClauseType>;
    condition: ConditionType;
}

interface IQueryFieldType {
    column: string;
    alias?: string;
    function?: IAggregationGroupType;
}

interface IOrderType {
    field: string;
    direction: OrderDirectionType;
}

interface IStatementLookupParams {
    where: IQueryType;
    select?: IQueryFieldType | IQueryFieldType[];
    groupBy?: IQueryFieldType | IQueryFieldType[];
    order?: IOrderType | IOrderType[];
    limit?: number;
    offset?: number;
}

const buildEquation = ({ field, value, operator = "eq", value_type }: IClauseType) => ({
    equation: {
        operand: {
            column: field,
        },
        compare: {
            [operator]: {
                operand: {
                    value: {
                        [value_type === ValueTypes.NUMBER ? ValueTypesToMerchantTableMappings.NUMBER : "string_value"]:
                            {
                                value: value_type === ValueTypes.NUMBER ? safeParseInt(value, -1) : value, // What should be the default value for an invalid numeric field
                            },
                    },
                },
            },
        },
    },
});

const mapFunctionField = (func: IAggregationGroupType) => {
    if (!func) {
        return null;
    }

    switch (func.type) {
        case AggregationFunctions.StringAggregation: {
            return {
                [func.type]: {
                    delimiter: func.string_aggr.delimiter,
                },
            };
        }
        default: {
            return null;
        }
    }
};

const mapSelectTermSettings = (terms: IQueryFieldType[]) =>
    terms.map((term) => ({
        column: term.column,
        alias: term.alias || "",
        function: mapFunctionField(term.function),
    }));

const buildSelectClause = (select: IQueryFieldType | IQueryFieldType[]) => {
    const terms: IQueryFieldType[] = nullish(select) ? [] : Array.isArray(select) ? select : [select];
    if (!terms.length) {
        return {
            all: true,
        };
    }

    return {
        terms: {
            terms: mapSelectTermSettings(terms),
        },
    };
};

const buildWhereClause = ({ rules, condition = "and" }: IQueryType) => ({
    [condition]: {
        expressions: (rules as IQueryType[]).map((rule) => {
            if (!rule.rules || !rule.rules.length) {
                return buildEquation(rule as any as IClauseType);
            }
            return buildWhereClause(rule);
        }),
    },
});

const buildFromClause = () => null;

const buildGroupByClause = (groupBy: IQueryFieldType | IQueryFieldType[]) => {
    const terms: IQueryFieldType[] = nullish(groupBy) ? [] : Array.isArray(groupBy) ? groupBy : [groupBy];
    if (!terms.length) {
        return null;
    }

    return {
        terms: {
            terms: mapSelectTermSettings(terms),
        },
    };
};

const buildOrderClause = (order?: IOrderType | IOrderType[]) => {
    const fields: IOrderType[] = nullish(order) ? [] : Array.isArray(order) ? order : [order];
    if (!fields.length) {
        return null;
    }

    return {
        by: fields.map(({ field, direction }) => ({
            column: field,
            sort: {
                order: direction,
            },
        })),
    };
};

const buildLimitClause = (val?: number) => (!nullish<number>(val) ? { val } : null);

const buildOffsetClause = (val?: number) => (!nullish<number>(val) ? { val } : null);

/**
 * Helper function to generate complete executesql statement
 * @param param0
 * @returns
 */
export const buildStatement = ({ where, select, order, groupBy, limit, offset }: IStatementLookupParams) => ({
    select_clause: buildSelectClause(select),
    from_clause: buildFromClause(),
    where_clause: buildWhereClause(where),
    group_by: buildGroupByClause(groupBy),
    order: buildOrderClause(order),
    limit: buildLimitClause(limit),
    offset: buildOffsetClause(offset),
});

/**
 * Populate WHERE clause with values
 * @param whereFields
 * @param dependenciesValues
 * @returns
 */
const buildWhereRules = (whereFields: ILookupWhereField[], dependenciesValues: IValueTypeMap = {}): IClauseType[] => {
    return whereFields.map(({ column }) => ({
        field: column.table_column,
        value: column.enforced_value || dependenciesValues[column.schema_column].value,
        operator: "eq",
        value_type: dependenciesValues[column.schema_column].type || ValueTypes.STRING,
    }));
};

/**
 * Build executesql statement based on lookup plan and specified WHERE values
 * TODO: Add unit test
 * @param lookupPlan
 * @param dependenciesValues
 * @param offset
 * @param limit
 * @returns
 */
export const buildStatementFromLookupPlan = (
    lookupPlan: ILookup,
    dependenciesValues: IValueTypeMap,
    offset?: number,
    limit?: number
) => {
    const statement = buildStatement({
        where: {
            rules: buildWhereRules(lookupPlan.where.fields, dependenciesValues),
            condition: "and",
        },
        select: (lookupPlan.select.terms ?? []).map((it) => ({
            column: it.column.table_column,
            ...(it.string_aggr
                ? { function: { type: AggregationFunctions.StringAggregation, string_aggr: it.string_aggr } }
                : {}),
        })),
        order: (lookupPlan.order?.by ?? []).map((it) => ({ field: it.column, direction: it.sort.order })),
        groupBy: (lookupPlan.group_by?.columns ?? []).map((it) => ({ column: it.table_column })),

        limit: limit || 100,
        offset: offset || 0,
    });

    return statement;
};
